import _ from '@sparta.global.vendors/lodash';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';

const ps$1 = new WeakMap();
const TRAILING_QUESTION_AMPERSAND_REGEX = /[?&]$/;

/**
 * Transport
 * An API that provides client functionality for transferring data between a client and a server.
 * @author Sergey Arakelov <sergey.arakelov@bankofamerica.com>
 */
class Transport {
  /**
   * Constructor
   * @param {object} option
   * @param {method} options.beforeSend
   * @param {boolean} options.cache
   * @param {boolean} options.crossDomain
   * @param {string|boolean} options.contentType
   * @param {object} options.data
   * @param {string} options.dataType
   * @param {object} options.headers
   * @param {method} options.method
   * @param {method} options.error
   * @param {method} options.success
   * @param {method} options.onTimeout
   * @param {number} options.timeout
   * @param {string} options.url
   * @param {object} options.xhrFields
   */
  constructor(opts, globalConfig) {
    const defaults = {
      cache: true,
      data: null,
      headers: {},
      // eslint-disable-line no-unused-vars
      method: 'GET',
      timeout: 0,
      xhrFields: {
        widthCredentials: false
      }
    };
    const options = {
      ...defaults,
      ...opts
    };
    const {
      beforeSend,
      cache,
      complete,
      data,
      error,
      method,
      success,
      timeout,
      xhrFields
    } = options; // eslint-disable-line max-len

    // defaulting contentType for non GET requests
    let {
      crossDomain,
      contentType
    } = options;
    if (typeof contentType === 'undefined' && ['GET', 'HEAD'].indexOf(method) === -1) contentType = 'application/x-www-form-urlencoded; charset=UTF-8';
    const {
      globalError,
      globalComplete,
      globalSuccess
    } = globalConfig;

    // create a new xhr instance
    const xhr = new XMLHttpRequest();

    // capturing the native xhr.getResponseHeader
    const nativeGetResponseHeader = xhr.getResponseHeader.bind(xhr);
    const nativeSetRequestHeader = xhr.setRequestHeader.bind(xhr);
    const nativeXHRAbort = xhr.abort.bind(xhr);
    xhr.abort = () => {
      this.options.shouldAbort = true;
    };

    // setting private data to a WeakMap
    ps$1.set(this, {
      xhr,
      status: '',
      ...options,
      globalConfig,
      nativeGetResponseHeader,
      nativeSetRequestHeader,
      nativeXHRAbort,
      completed: false,
      called: {}
    });
    const urlObj = Transport.getUrlInfo(this.options.url);
    const reqData = {
      ...this.options.data
    };

    // auto determine cross domain when its undefined
    if (typeof crossDomain === 'undefined') crossDomain = window.location.origin !== urlObj.origin;

    // applying the args
    let urlRequestString = `${this.options.url}${urlObj.search ? '&' : '?'}`;

    // when contentType is application/data and data is a string, just send it as is
    let payload = contentType && contentType.indexOf('application/json') !== -1 && typeof data === 'string' ? data : this.buildUrlQueryString(data);
    payload = payload === '' ? null : payload;

    // apply caching
    if (['GET', 'HEAD'].indexOf(method) > -1 && (cache === false || this.dataType === 'script')) {
      reqData._ = new Date().getTime();
    }

    // attach urlQueryString to urlRequestString for GET requests
    if (['GET', 'HEAD'].indexOf(method) > -1 && urlRequestString.length) {
      urlRequestString = `${urlRequestString}${urlRequestString.match(/\?$/) || urlObj.search ? '' : '?'}${this.buildUrlQueryString(reqData)}`;
    }

    // attach timestamp to query to avoid caching
    urlRequestString = urlRequestString.replace(TRAILING_QUESTION_AMPERSAND_REGEX, '').replace(TRAILING_QUESTION_AMPERSAND_REGEX, '');

    // faking xhr.setRequestHeader to allow headers to be set in beforeSend
    this.xhr.setRequestHeader = this.setRequestHeader.bind(this);

    // handle beforeSend
    if (beforeSend && typeof beforeSend === 'function') {
      const beforeSendResult = beforeSend.call(this.options, this.xhr, this.options);
      if (beforeSendResult === false) return;
      this.xhr.abort = nativeXHRAbort;
      ps$1.set(this, {
        ...options,
        ...ps$1.get(this),
        ...this.options
      });
      if (this.options.url !== options.url) urlRequestString = this.options.url;
    }

    // reset after processing beforeSend
    this.xhr.setRequestHeader = nativeSetRequestHeader;
    const reqHeaders = {
      ...this.options.headers
    };

    // X-Requested-With
    if (!crossDomain && !reqHeaders['X-Requested-With']) {
      reqHeaders['X-Requested-With'] = 'XMLHttpRequest';
    }

    // set request the content type
    // ignore if headers['Content-Type'] already exists
    if (!reqHeaders['Content-Type'] && contentType && contentType !== false) {
      reqHeaders['Content-Type'] = contentType;
    }

    // open the request
    this.xhr.open(method, urlRequestString, true);

    // Set headers
    for (const i in reqHeaders) {
      // eslint-disable-line
      nativeSetRequestHeader(i, reqHeaders[i]);
    }

    // adding some protection
    this.xhr.withCredentials = xhrFields.withCredentials;

    // faking xhr.getResponseHeader to suppress the unsafe header warning
    this.xhr.getResponseHeader = this.getResponseHeader.bind(this);

    // handle xhr state change
    this.xhr.onreadystatechange = () => {
      // Handling success
      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 200 && xhr.status < 300 || xhr.status === 304) {
        this.onSuccess(success);
        if (globalSuccess && globalSuccess.doc && globalSuccess.cb) {
          this.onGlobalSuccess(globalSuccess.doc, globalSuccess.cb);
        }
        ps$1.get(this).completed = true;
      }

      // Handling errors
      const readyStateErrorCheck = (xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED || xhr.readyState === XMLHttpRequest.DONE) && xhr.status >= 400;
      const cancelledStateCheck = xhr.readyState === XMLHttpRequest.DONE && xhr.status === 0;
      if (cancelledStateCheck || readyStateErrorCheck) {
        // call onError when it hasnt been aborted
        if (!this.options.called.error) this.onError(error);
        if (!this.options.called.globalError && globalError && globalError.doc && globalError.cb) {
          this.onGlobalError(globalError.doc, globalError.cb);
        }
      }

      // Handling complete
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (!this.options.called.complete) this.onComplete(complete);
        if (!this.options.called.globalComplete && globalComplete && globalComplete.doc && globalComplete.cb) {
          this.onGlobalComplete(globalComplete.doc, globalComplete.cb);
        }
      }
    };
    this.xhr.ontimeout = () => {
      this.onError(error, 'timeout');
      if (globalError && globalError.doc && globalError.cb) {
        this.onGlobalError(globalError.doc, globalError.cb, 'timeout');
      }
    };

    // set timeout
    this.xhr.timeout = timeout;

    // sending the request
    if (!this.options.shouldAbort) this.xhr.send(payload);
  }

  /**
   * fakeAbort
   * The purpose of this is to allow user to abort in the beforeSend
   */
  fakeAbort() {
    this.options.shouldAbort = true;
  }

  /**
   * Abort the request
   */
  abort() {
    this.options.called.error = true;
    this.options.called.complete = true;
    this.xhr.abort();
    this.onError(this.options.error, 'abort');
    this.onComplete(this.options.complete);
    if (!this.options.called.globalComplete && this.options.globalConfig.globalComplete && this.options.globalConfig.globalComplete.doc && this.options.globalConfig.globalComplete.cb) {
      this.onGlobalComplete(this.options.globalConfig.globalComplete.doc, this.options.globalConfig.globalComplete.cb);
    }
  }

  /**
   * buildUrlQueryString
   * @param {object} data
   * @param {string} prefix
   */
  buildUrlQueryString(obj, prefix) {
    const str = [];
    for (const p in obj) {
      // eslint-disable-line no-restricted-syntax
      if (Object.prototype.hasOwnProperty.call(obj, p)) {
        const k = prefix ? `${prefix}[${p}]` : p;
        const v = obj[p];
        const qStr = v !== null && typeof v === 'object' ? this.buildUrlQueryString(v, k) : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
        str.push(qStr);
      }
    }
    return str.join('&');
  }

  /**
   * Get absolute url
   * @param {string} url URL string
   */
  static getUrlInfo(url) {
    // eslint-disable-line class-methods-use-this
    let a;
    if (!a) a = document.createElement('a');
    a.href = url;
    return a;
  }

  /**
   * onGlobalSuccess
   * Global error hook
   * @param {object} doc Window document
   * @param {method} onGlobalSuccessCB Callback method
   */
  onGlobalSuccess(doc, onGlobalSuccessCB) {
    if (doc === document && typeof onGlobalSuccessCB === 'function') {
      onGlobalSuccessCB.call(doc, {
        currentTarget: doc,
        target: doc
      }, this.xhr, this.options, this.getXHRData());
    }
  }

  /**
   * onGlobalError
   * Global error hook
   * @param {object} doc Window document
   * @param {method} onGlobalErrorCB Callback method
   */
  onGlobalError(doc, onGlobalErrorCB, status = 'error') {
    this.options.called.globalError = true;
    if (doc === document && typeof onGlobalErrorCB === 'function') {
      onGlobalErrorCB.call(doc, {
        currentTarget: doc,
        target: doc
      }, this.xhr, this.options, status);
    }
  }

  /**
   * onGlobalComplete
   * Global error hook
   * @param {object} doc Window document
   * @param {method} onGlobalCompleteCB Callback method
   */
  onGlobalComplete(doc, onGlobalCompleteCB) {
    this.options.called.globalComplete = true;
    if (doc === document && typeof onGlobalCompleteCB === 'function') {
      onGlobalCompleteCB.call(doc, {
        currentTarget: doc,
        target: doc
      }, this.xhr, this.options);
    }
  }

  /**
   * onError handler
   * @param {method} onErrorCB User defined onError callback
   */
  onError(onErrorCB, status = 'error') {
    this.options.called.error = true;
    if (typeof onErrorCB === 'function') {
      this.status = status;
      const statusText = this.xhr.statusText === '' ? status : this.xhr.statusText;
      onErrorCB(this.xhr, this.status, statusText);
    }
    return null;
  }

  /**
   * onComplete handler
   * Called after error or success callbacks
   * @param {method} onCompleteCB User defined onComplete callback
   */
  onComplete(onCompleteCB) {
    this.options.called.complete = true;
    if (onCompleteCB && typeof onCompleteCB === 'function') {
      onCompleteCB.call(this.options, this.xhr, this.status);
    }
    return null;
  }

  /**
   * onSuccess handler
   * @param {method} onSuccessCB User defined onSuccess callback
   */
  onSuccess(onSuccessCB) {
    this.options.called.success = true;
    const data = this.getXHRData();

    // call users onSuccessCB method
    if (onSuccessCB && typeof onSuccessCB === 'function') {
      this.status = 'success';
      return onSuccessCB.call(this.options, data, this.status, this.xhr);
    }
    return null;
  }

  /**
   * Get the conformed xhr data based on the dataType or content-type
   */
  getXHRData() {
    const contentTypes = {
      json: 'application/json',
      xml: 'application/xml',
      html: 'text/html',
      script: 'application/javascript'
    };
    if (!this.dataType) {
      for (const type in contentTypes) {
        // eslint-disable-line no-restricted-syntax
        if (this.xhr.getResponseHeader('Content-Type')?.indexOf(type) !== -1) this.dataType = type;
      }
    }
    if (this.xhr.responseText === '') return undefined;

    // handling the dataType
    switch (this.dataType) {
      case 'json':
        try {
          return JSON.parse(this.xhr.responseText);
        } catch (e) {
          return undefined;
        }
      case 'xml':
        return this.xhr.responseXML;
      case 'script':
      case 'html':
      default:
        return this.xhr.responseText;
    }
  }

  /**
   * Cross browser XML parsing
   * @param {string} xmlData XML Data
   */
  parseXML(xmlData) {
    // eslint-disable-line class-methods-use-this
    let xml;
    if (!xmlData || typeof xmlData !== 'string') return null;

    // Support: IE 9 - 11 only
    // IE throws on parseFromString with invalid input.
    try {
      xml = new window.DOMParser().parseFromString(xmlData, 'text/xml');
    } catch (e) {/* noop */}
    if (!xml || xml.getElementsByTagName('parsererror').length) {
      throw new Error(`Invalid XML: ${xmlData}`);
    }
    return xml;
  }

  /**
   * setRequestHeader
   * Set a request header
   * @param {string} key Key Name
   * @param {string} val Value
   */
  setRequestHeader(key, val) {
    this.options.headers[key] = val;
  }

  /**
   * Content headers value lookup by key name
   * @param {string} key Key name
   */
  getResponseHeader(key) {
    const rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg;
    const responseHeaders = {};
    let match;
    while (match = rheaders.exec(this.xhr.getAllResponseHeaders())) {
      // eslint-disable-line no-cond-assign,max-len
      responseHeaders[match[1].toLowerCase()] = match[2];
    }
    match = responseHeaders[key.toLowerCase()];
    return match;
  }

  /**
   * Get the current options for the XHR request
   */
  get options() {
    return ps$1.get(this);
  }

  /**
   * Set the current status of the xhr request
   */
  set status(status) {
    ps$1.get(this).status = status;
  }

  /**
   * Get the current status of the xhr request
   */
  get status() {
    return ps$1.get(this).status;
  }

  /**
   * Set the dataType
   */
  set dataType(type) {
    this.options.dataType = type;
  }

  /**
   * Get the dataType
   */
  get dataType() {
    return this.options.dataType;
  }

  /**
   * Get the XHR object
   */
  get xhr() {
    return ps$1.get(this).xhr;
  }
}

const CACHE_PATH_NAMESPACE = 'transport-cache:';
const ps = new WeakMap();

/**
 * if ttl is 0, use sessionStorage
 * if ttl is greater than 1, use localStorage
 * if ttl is 1 use spartaStoreUtility
 * @param {object} requestObj e.g. { ttl: 0, cacheKey: 'mykey' }
 */
function setCache(requestObj = {}) {
  const ttl = parseInt(requestObj.ttl, 10);
  const cacheKey = `${CACHE_PATH_NAMESPACE}${requestObj.cachePath}`;
  const storageType = ttl === 0 ? 'sessionStorage' : 'localStorage';
  return ttl === 1 && spartaStoreUtility.addData(cacheKey, requestObj) || spartaStoreUtility.persist(cacheKey, requestObj, storageType);
}

/**
 * processJWTBeforeSend
 * @param {object} jwt JWT configuration
 * @param {object} xhr XHR object
 * @param {object} options Options
 */
function processJWTBeforeSend(jwt, xhr, options, customHeaders) {
  const {
    name: tokenName,
    result: tokenResult
  } = jwt;
  if (!customHeaders?.Authorization) {
    // getting all jwt tokens
    const tokens = spartaStoreUtility.getPersisted('jwt');

    // looking for existing token
    const JWToken = tokens[tokenResult || tokenName];

    // attach token if it exists
    if (JWToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${JWToken}`);
    }
  }
  if (!xhr.__SPARTA_SKIP__PageIdHeader && !customHeaders?.__PageIdHeader && options.method && options.method.toLowerCase() === 'post') {
    // getting CSRF token for Wealth specific Apps / Widgets
    const csrfToken = spartaStoreUtility.getData('csrfToken');
    if (csrfToken) {
      xhr.setRequestHeader('__PageIdHeader', csrfToken);
    }
  }
}

/**
 * processJWTOnSuccess
 * @param {object} jwt JWT configuration
 * @param {*} data XHR response data
 * @param {string} status XHR response status
 * @param {object} xhr XHR object
 */
function processJWTOnSuccess(jwt, data) {
  const {
    name: tokenName,
    lookup: tokenLookup,
    result: tokenResult
  } = jwt;

  // look for token based on lookup path (if it exists) otherwise look up by tokenName
  const JWToken = _.isString(tokenLookup) ? _.get(data, tokenLookup) : data[tokenName];

  // cache the jwt token if it comes back in the response
  if (JWToken) {
    // getting all jwt tokens
    const tokens = spartaStoreUtility.getPersisted('jwt');
    const existingToken = tokens[tokenResult || tokenName];

    // check for existing token matching the token in the response
    if (existingToken !== JWToken) {
      // save the token
      tokens[tokenResult || tokenName] = JWToken;

      // persist jwt contents
      spartaStoreUtility.persist('jwt', tokens);
    }
  }
}

/**
 * @param {string} method e.g. 'GET'
 * @param {string} url e.g. 'https://bankofamerica.com/move-money/one-time-transfer/REST-service'
 * @return {string} e.g. 'GET.move-money.one-time-transfer.REST-service'
 */
function getCachePath(method, url) {
  const urlObj = Transport.getUrlInfo(url);
  const path = url.match(/(?:https?:\/\/[^\/]*\/)(.*)/) || false;
  return path ? `${method}.${urlObj.host.replace(':', '.')}${urlObj.pathname.replace(/\//g, '.')}${urlObj.search}` : '';
}
class SpartaTransportUtility {
  /**
   * Performs an AJAX request and caches the result
   * @param {object} subscription
   * @param {object} subscription.data an object to be set to the URL as data
   * @param {boolean} subscription.cache http://api.jquery.com/jquery.ajax/
   * @param {boolean|string} subscription.contentType http://api.jquery.com/jquery.ajax/
   * @param {boolean} subscription.crossDomain http://api.jquery.com/jquery.ajax/
   * @param {string} subscription.dataType http://api.jquery.com/jquery.ajax/
   * @param {function} subscription.error http://api.jquery.com/jquery.ajax/
   * @param {object} subscription.headers http://api.jquery.com/jquery.ajax/
   * @param {object} subscription.jwt JWT configuration object
   * @param {string} subscription.jwt.name The key name the JWT will be sent as
   * @param {string} subscription.jwt.lookup The lookup path for finding the JWT from response, will be attached to the next request
   * @param {string} subscription.jwt.result The key name the JWT will be persisted with
   * @param {string} subscription.method http://api.jquery.com/jquery.ajax/
   * @param {function} subscription.success http://api.jquery.com/jquery.ajax/ fires when a successful request/cache pull is complete. It will be passed the result/cached data
   * @param {number} subscription.timeout http://api.jquery.com/jquery.ajax/
   * @param {string} subscription.url the URL/REST object to make a request to
   * @param {object} subscription.xhrFields http://api.jquery.com/jquery.ajax/
   * @return {boolean} false if not enough data exists for ajax request
   */
  constructor(sub = {}) {
    if (!spartaStoreUtility.getPersisted('jwt')) spartaStoreUtility.persist('jwt', {});
    const defaults = {
      error: () => {
        /* noop */
      },
      // eslint-disable-line no-unused-vars
      cache: true,
      jwt: {},
      method: 'GET',
      success: () => {
        /* noop */
      },
      url: window.location.href,
      ignorePreload: false
    };
    const subscription = {
      ...defaults,
      ...sub
    };
    const {
      beforeSend,
      cache,
      jwt,
      method,
      success,
      url,
      ignorePreload
    } = subscription;

    // request object
    const requestObj = {
      cachePath: getCachePath(method, url),
      subscription
    };

    // Get cached data
    const cachedData = this.isCached(requestObj);
    const preloaded = this.isPreloaded(requestObj);
    const transportOptions = {
      config: {
        ...defaults,
        ...subscription,
        beforeSend(xhr, options) {
          // process jwt before sending request
          processJWTBeforeSend(jwt, xhr, options, subscription.headers);
          // call user defined callback
          if (beforeSend && typeof beforeSend === 'function') return beforeSend.call(this, xhr, this);
        },
        success(d, status, xhr) {
          const data = d;

          // process jwt on success of request
          if (data) processJWTOnSuccess(jwt, data);

          // time to live
          const ttl = xhr.getResponseHeader('ttl');
          Object.assign(requestObj, {
            data,
            ttl,
            timestamp: Date.now()
          });

          // false for NaN
          if (parseInt(ttl, 10) >= 0 && cache !== false) setCache(requestObj);

          // call the user defined success callback
          if (success && typeof success === 'function') success.call(this, data, status, xhr);
        }
      },
      globalConfig: ps.get(SpartaTransportUtility)
    };
    ps.set(this, {
      transportOptions
    });

    // has this been kicked off by a webworker?
    if (preloaded && !ignorePreload) return false;
    if (cachedData) {
      success.call(this, cachedData.data);
    } else {
      ps.get(this).transport = new Transport({
        ...transportOptions.config
      }, transportOptions.globalConfig);
    }
    return this;
  }

  /**
   * abort
   * Abort a transport
   */
  abort() {
    ps.get(this).transport.abort();
  }

  /**
   * List of events
   */
  get EVENTS() {
    // eslint-disable-line class-methods-use-this
    return {
      CREATE_EVENT: 'TRANSPORT.createdTransport',
      UPDATE_EVENT: 'TRANSPORT.updatedTransport',
      DELEGATE_EVENT: 'TRANSPORT.delegatedTransport',
      FETCH_EVENT: 'TRANSPORT.fetchedTransport',
      REFRESH_EVENT: 'TRANSPORT.refreshedTransport'
    };
  }

  /**
   * @param {object} requestObj e.g. { ttl: 0, cachePath: 'GET.move-money.one-time-transfer.REST-service' }
   * @return {*} result will be null if data not cached and will
   * be the stored data if it is cached
   */
  isCached(requestObj = {}) {
    // eslint-disable-line class-methods-use-this
    const cacheKey = CACHE_PATH_NAMESPACE + requestObj.cachePath;
    let storedData = spartaStoreUtility.getData(cacheKey) || spartaStoreUtility.getPersisted(cacheKey, 'sessionStorage');
    let result;
    storedData = Array.isArray(storedData) ? storedData[0] : storedData;
    if (!storedData) {
      return null;
    }

    // i.e. 0 (session) || 1 (page)
    if (storedData.ttl <= 1) {
      result = storedData;
    } else {
      const currentTime = Date.now();
      const cachedTime = storedData.timestamp;
      const diffTime = (currentTime - cachedTime) / 1000;

      // compare the ttls to see
      result = diffTime < storedData.ttl ? storedData : null;
    }
    return result;
  }

  /**
   * isPreloded
   * @param {any} [requestObj={}]
   * @returns {boolean} false if not part of the api-preload object, true if it is
   *
   * @memberOf SpartaTransportUtility
   */
  isPreloaded(requestObj = {}) {
    // eslint-disable-line class-methods-use-this
    window.sparta = window.sparta || {};
    window.sparta.APIWorkers = window.sparta.APIWorkers || [];
    const workers = window.sparta.APIWorkers;
    const worker = _.find(workers, {
      key: requestObj.cachePath
    });
    if (!worker) return false;
    const onError = e => {
      const {
        transportOptions
      } = ps.get(this);
      ps.get(this).transport = new Transport({
        ...transportOptions.config
      }, transportOptions.globalConfig);
    };
    if (!worker.data) {
      document.addEventListener(`${requestObj.cachePath}:TIMEOUT`, onError, false);
      document.addEventListener(`${requestObj.cachePath}:ERROR`, onError, false);
      document.addEventListener(`${requestObj.cachePath}:LOADED`, () => requestObj.subscription.success(worker.data), false);
    } else {
      // return the response
      requestObj.subscription.success(worker.data);
    }
    return !!worker;
  }
}

/**
 * getGlobalConfig
 * Get the global config
 */
function getGlobalConfig() {
  let config = ps.get(SpartaTransportUtility);
  if (!config) {
    ps.set(SpartaTransportUtility, {
      options: {}
    });
    config = ps.get(SpartaTransportUtility);
  }
  return config;
}

/**
 * globalHandler
 * @param {string} lookup Global handler lookup name
 */
function globalHandler(lookup) {
  return (doc, cb) => {
    const globalConfig = getGlobalConfig();
    globalConfig[`global${lookup}`] = {
      doc,
      cb
    };
  };
}

/**
 * globalError
 * Transport global error handler
 */
const globalError = globalHandler('Error');

/**
 * globalSuccess
 * Transport global success handler
 */
const globalSuccess = globalHandler('Success');

/**
 * globalComplete
 * Transport global complete handler
 */
const globalComplete = globalHandler('Complete');

/**
 * setup
 * @param {options} options Transport option configuration
 */
function setup(options = {}) {
  getGlobalConfig().options = options;
}

/**
 * subscribe
 * @param {object} options Same options as the SpartaTransportUtility
 */
function subscribe(options = {}) {
  const extendedOptions = {
    ...getGlobalConfig().options,
    ...options
  };
  return new SpartaTransportUtility(extendedOptions);
}

/**
 * Performs an AJAX request and returns the result ( takes the same arguments as subscribe() )
 * Essentially just a wrapper for subscribe
 * Same options as the SpartaTransportUtility
 * @param {object} subscription
 */
function once(options = {}) {
  const req = options;
  req.cache = false;
  return this.subscribe(req);
}
var index = {
  globalError,
  globalComplete,
  globalSuccess,
  getGlobalConfig,
  once,
  setup,
  subscribe
};

export { index as default, getGlobalConfig, globalComplete, globalError, globalSuccess, once, setup, subscribe };
